<script setup lang="ts">
import { BaseObjListView, ContentComp, DropdownSelect, ToolbarComp } from '@/components';
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import { useKeyboardListener, useTaskLoader } from '@/composeables';
import { RaceContestantPointsDao } from '@/dao';
import { DropdownItemButton, type SelectLine } from '@/objects';
import { RaceContestant, RaceContestantPoints, TimepenaltyRequest, type RaceContestantDetails } from '@/objects/generated';
import { useEventCommonStoreRef, useGuiStore, useUtilStore } from '@/stores';
import { propertyof } from '@/utilities';
import { computed, onMounted, ref, watch } from 'vue';

import type BaseObjListViewCompExpose from '@/objects/BaseObjListViewCompExpose';
import type { IGridRowCssRule } from '@/components/Grid/Classes/IGridRowCssRule';

const listComp = ref<BaseObjListViewCompExpose<RaceContestantDetails> | null>(null);

const contestant = defineModel<RaceContestantDetails>({ required: true });

const changed = defineModel<boolean>('changed', { default: false });

const { AddLoader, LoadState } = useTaskLoader();

const { Races } = useEventCommonStoreRef();

const data = ref<RaceContestantPoints[]>([]);

// Set the initial value for the contestant
const firstRaceContestant: RaceContestant | null = contestant.value.RaceContestants.length > 0 ? contestant.value.RaceContestants[0] : null;

// The currently selected race contestant
const selectedRaceContestantId = ref<number | null>(firstRaceContestant?.ID ?? null);

// We create the datasource for the dropdown with the race contestants
const computedRacecontestantsAsSelectline = computed(() => {
    return contestant.value.RaceContestants.map((rc) => {
        return {
            value: rc.ID,
            text: Races.value.find((x) => x.ID == rc.RaceID)?.Name ?? '',
        } as SelectLine;
    }) as SelectLine[];
});

/**
 * Load the data for the view. We load all the data from the backend
 */
const LoadData = () => {
    AddLoader(async () => {
        data.value =
            (await RaceContestantPointsDao.GetRaceContestantPointsForContestant(contestant.value.EventID, contestant.value.ContestantID)) ??
            [];
        listComp.value?.dataUpdated();
    }, '');
};

/**
 * Add a new time for the selected contestant
 */
const addPoints = () => {
    if (selectedRaceContestantId.value == null || selectedRaceContestantId.value <= 0) {
        throw new Error('No valid racecontestant selected');
    }

    // Add a new item to the data
    const newItem = RaceContestantPoints.FromJsValues({
        RaceContestantID: selectedRaceContestantId.value,
        EntityState: 'Added',
        ID: useUtilStore().nextFakeId(),
        EventID: contestant.value.EventID,
        PointTypeID: 1, // This is the type 'Poeng',
    });

    // We add the new item to the data
    data.value.push(newItem);

    changed.value = true;

    // We refresh the grid
    listComp.value?.dataUpdated();
};

/**
 * Map the columns to the grid
 * @param columns
 */
const handleCustomColumns = (columns: GridColumnOptions<RaceContestantPoints>[]) => {
    // // We change the cellrenderer for the time column for our custom component that wraps the DateComp with a edit button
    let column = columns.find((c) => c.dataField == propertyof<RaceContestantPoints>('Points'));

    if (column != null) {
        column.aggregate = {
            type: 'sum',
            decimals: 2,
            aggregate: () => {
                // We return the sum of all the penalties of the rows that has the correct selectedRaceContestant
                return data.value
                    .filter((x) => x.RaceContestantID == selectedRaceContestantId.value)
                    .reduce((acc, val) => acc + val.Points, 0);
            },
        };
    }

    return columns;
};

/**
 * The row class rules for the grid
 * We want to show the current active times as Green and the times that are not active as red. Disabled times we show as grey
 */
const rowClassRules: IGridRowCssRule<RaceContestantPoints>[] = [
    {
        className: 'grid-row-success',
        func: (data: RaceContestantPoints) => data.RaceContestantID == selectedRaceContestantId.value,
    },
    {
        className: 'grid-row-warning',
        func: (data: RaceContestantPoints) => data.RaceContestantID != selectedRaceContestantId.value,
    },
];

// The toolbar actions
const toolbarActions = [
    DropdownItemButton.create({
        buttonType: 'Primary',
        translationkey: 'contestanttimepenalty.addnew',
        title: 'contestanttimepenalty.addnewtitle',
        icon: 'fa-plus',
        action: () => addPoints(),
    }),
    DropdownItemButton.create({
        buttonType: 'Secondary',
        translationkey: 'contestanttimepenalty.delete',
        title: 'contestanttimepenalty.deletetitle',
        icon: 'fa-xmark',
        action: () => listComp.value?.deleteSelected(),
    }),
];

const toolbarRightActions = [
    DropdownItemButton.create({
        buttonType: 'Secondary',
        translationkey: 'general.refresh',
        icon: 'fa-sync',
        action: () => LoadData(),
    }),
];

/**
 * Callback when the visibility of the component changes
 * @param isVisible
 */
const onIsVisible = (isVisible: boolean) => {
    // We add a temporary keyboard shortcut for CTRL+INSERT to add a new time
    if (isVisible) {
        useKeyboardListener().addKeyboardShortcut({ alias: 'contestanttimepenaltyaddnew', on: 'CTRL+INSERT', action: () => {} });
    } else {
        // We make sure we remove the keyboard shortcut when the component is not visible
        useKeyboardListener().removeKeyboardShortcut('contestanttimepenaltyaddnew');
    }
};

// Just trigger a data updated when we change the race contestants. We do this so we also run the row rules
watch(selectedRaceContestantId, () => listComp.value?.dataUpdated());

/**
 * Make sure we set the selectedRaceContestant when the selectedRaceContestantId changes
 */
watch(contestant.value.RaceContestants, (nv, ov) => {
    if (selectedRaceContestantId.value == null && nv?.length > 0) {
        selectedRaceContestantId.value = nv[0].ID;
    }
});

onMounted(() => {
    // We load the data when the component is mounted
    LoadData();
});

defineExpose({
    /**
     * Save the list
     */
    save: () => {
        listComp.value?.saveList();
    },
});
</script>
<template>
    <ContentComp
        v-if="contestant && selectedRaceContestantId != null"
        class="p-0"
        :height="50"
        :load-state="LoadState"
        :show-as-modal="true"
        @is-visible="onIsVisible"
    >
        <BaseObjListView
            ref="listComp"
            class="overflow-auto h-100 p-0"
            :baseType="RaceContestantPoints"
            v-model="data"
            :gui="useGuiStore().RaceContestantPoints"
            :select-all="true"
            :hide-messages="true"
            :hide-toolbar="true"
            :customColumns="handleCustomColumns"
            :rowClassRules="rowClassRules"
            resize-strategy="sizeColumnsToFit"
            :hide-statusbar="true"
        >
            <ToolbarComp>
                <template #first>
                    <DropdownSelect
                        v-if="computedRacecontestantsAsSelectline.length > 1"
                        :data="computedRacecontestantsAsSelectline"
                        v-model="selectedRaceContestantId"
                    />
                    <ButtonGroup :actions="toolbarActions" />
                </template>
                <template #last>
                    <ButtonGroup :actions="toolbarRightActions" />
                </template>
            </ToolbarComp>
        </BaseObjListView>
    </ContentComp>
</template>
